import { AppError } from "@nwga/contracts/errors";
import type { Id } from "@nwga/contracts/id";
import { type CountySlug, countyLabel } from "@nwga/contracts/region";
import type { JobRow } from "@nwga/db/schema";
import type {
	BusinessId,
	BusinessSummary,
	UserId,
} from "../businesses/domain.ts";

/**
 * The job listing entity and the rules that govern it.
 *
 * Pure data and pure functions. Anything that needs a database or a request
 * belongs in `service.ts`; anything that is simply true about a listing belongs
 * here, where it can be unit-tested without infrastructure.
 */

export type JobId = Id<"job">;

export type EmploymentType =
	| "full_time"
	| "part_time"
	| "contract"
	| "temporary"
	| "internship"
	| "seasonal";

export type WorkArrangement = "onsite" | "hybrid" | "remote";
export type PayPeriod = "hourly" | "annual";
export type JobStatus = "draft" | "published" | "closed";

export interface JobPay {
	readonly minCents: number | null;
	readonly maxCents: number | null;
	readonly period: PayPeriod;
}

export interface Job {
	readonly id: JobId;
	readonly businessId: BusinessId;
	readonly slug: string;
	readonly title: string;
	readonly description: string;
	readonly employmentType: EmploymentType;
	readonly workArrangement: WorkArrangement;
	readonly county: CountySlug | null;
	readonly city: string | null;
	readonly pay: JobPay | null;
	readonly applyUrl: string | null;
	readonly applyEmail: string | null;
	readonly status: JobStatus;
	readonly publishedAt: Date | null;
	readonly expiresAt: Date | null;
	readonly createdBy: UserId;
	readonly createdAt: Date;
	readonly updatedAt: Date;
}

/** A listing with the business it belongs to, which is how it is always shown. */
export interface JobWithBusiness extends Job {
	readonly business: BusinessSummary;
}

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
	full_time: "Full-time",
	part_time: "Part-time",
	contract: "Contract",
	temporary: "Temporary",
	internship: "Internship",
	seasonal: "Seasonal",
};

export const WORK_ARRANGEMENT_LABELS: Record<WorkArrangement, string> = {
	onsite: "On-site",
	hybrid: "Hybrid",
	remote: "Remote",
};

export function toJob(row: JobRow): Job {
	return {
		id: row.id,
		businessId: row.businessId,
		slug: row.slug,
		title: row.title,
		description: row.description,
		employmentType: row.employmentType,
		workArrangement: row.workArrangement,
		county: row.countySlug,
		city: row.city,
		pay:
			row.payPeriod === null
				? null
				: {
						minCents: row.payMinCents,
						maxCents: row.payMaxCents,
						period: row.payPeriod,
					},
		applyUrl: row.applyUrl,
		applyEmail: row.applyEmail,
		status: row.status,
		publishedAt: row.publishedAt,
		expiresAt: row.expiresAt,
		createdBy: row.createdBy,
		createdAt: row.createdAt,
		updatedAt: row.updatedAt,
	};
}

/**
 * Whether a listing is visible to people who are not part of the business.
 *
 * Note that an expired listing is *not* public even while its row still says
 * `published` — expiry is enforced here as well as by the background task, so a
 * late-running worker never leaves a stale listing on the board.
 */
export function isPublic(
	job: Pick<Job, "status" | "expiresAt">,
	now = new Date(),
): boolean {
	if (job.status !== "published") return false;
	return job.expiresAt === null || job.expiresAt > now;
}

export function isExpired(
	job: Pick<Job, "expiresAt">,
	now = new Date(),
): boolean {
	return job.expiresAt !== null && job.expiresAt <= now;
}

/**
 * Where the role is worked, in one line.
 *
 * A remote listing has no county, so falling back to the business's location
 * would actively mislead — hence the explicit branch.
 */
export function locationLabel(
	job: Pick<Job, "workArrangement" | "county" | "city">,
): string {
	if (job.workArrangement === "remote") return "Remote";

	const place =
		job.city !== null && job.city.length > 0
			? `${job.city}, GA`
			: job.county === null
				? "Northwest Georgia"
				: countyLabel(job.county);

	return job.workArrangement === "hybrid" ? `Hybrid — ${place}` : place;
}

const CURRENCY = new Intl.NumberFormat("en-US", {
	style: "currency",
	currency: "USD",
	maximumFractionDigits: 0,
});

/** "$18–$24 / hour", "$52,000 / year", or null when pay is not stated. */
export function payLabel(pay: JobPay | null): string | null {
	if (pay === null) return null;
	if (pay.minCents === null && pay.maxCents === null) return null;

	const unit = pay.period === "hourly" ? "hour" : "year";
	const format = (cents: number) => CURRENCY.format(cents / 100);

	if (
		pay.minCents !== null &&
		pay.maxCents !== null &&
		pay.minCents !== pay.maxCents
	) {
		return `${format(pay.minCents)}–${format(pay.maxCents)} / ${unit}`;
	}

	const single = pay.minCents ?? pay.maxCents;
	return single === null ? null : `${format(single)} / ${unit}`;
}

export interface JobDraft {
	readonly workArrangement: WorkArrangement;
	readonly county: CountySlug | null;
	readonly applyUrl: string | null;
	readonly applyEmail: string | null;
	readonly payMinCents: number | null;
	readonly payMaxCents: number | null;
}

/**
 * Invariants that span fields, which a per-field schema cannot express.
 *
 * Checked on every write rather than only at publish time, so a draft can never
 * be saved in a state that would fail later — a listing that cannot be
 * published is worse than one that was rejected up front.
 */
export function assertValidDraft(draft: JobDraft): void {
	if (draft.applyUrl === null && draft.applyEmail === null) {
		throw AppError.unprocessable(
			"Give applicants a way to apply: either a link or an email address.",
			{ reason: "job.no_apply_method" },
		);
	}

	if (draft.workArrangement !== "remote" && draft.county === null) {
		throw AppError.unprocessable(
			"Choose the county where this role is based.",
			{
				reason: "job.missing_county",
			},
		);
	}

	if (
		draft.payMinCents !== null &&
		draft.payMaxCents !== null &&
		draft.payMinCents > draft.payMaxCents
	) {
		throw AppError.unprocessable(
			"The minimum pay cannot be higher than the maximum.",
			{
				reason: "job.pay_range_inverted",
			},
		);
	}
}

/** Listings expire after 60 days unless the poster chooses otherwise. */
export const DEFAULT_LISTING_DAYS = 60;

export function defaultExpiry(from = new Date()): Date {
	return new Date(from.getTime() + DEFAULT_LISTING_DAYS * 24 * 60 * 60 * 1000);
}
